import React, { useCallback, useEffect, useState } from 'react'
import { mdiMagnify, mdiPlusCircle } from '@mdi/js'
import { TextField } from '@material-ui/core'
import { Formik, Form as FormikContainer } from 'formik'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../store/hooks'
import Section, { SectionBox, SectionOption } from '../../../components/Section'
import Modal from '../../../components/Modal'
import Form from '../../../components/Form'
import AgrupamentoComponent from './Components/AgrupamentoComponent'
import ConfigPermissaoListaModal from './Components/ConfigPermissaoListaModal'
import getListas from './scripts/getListas'
import getConfig from './scripts/getConfig'
import saveLista from './scripts/saveLista'
import removeLista from './scripts/removeLista'
import saveItem from './scripts/saveItem'
import removeItem from './scripts/removeItem'
import saveOrdem from './scripts/saveOrdem'
import filterListas from './scripts/filterListas'
import { setCurrentItem, setCurrentLista, setFieldFiltered } from './Reducers/LinksTutoriaisReducer'
import { LinksTutoriaisItemType, LinksTutoriaisListaType } from './types'

const LinksTutoriais: React.FC = () => {
    const dispatch = useDispatch()
    const { userData } = useAppSelector(state => state.auth)
    const {
        lista, listaFiltered, config, fieldFiltered, currentLista, currentItem,
        requestGet, requestSave, requestRemove, requestConfig
    } = useAppSelector(state => state.LinksTutoriaisReducer)

    const [openCriarLista, setOpenCriarLista] = useState(false)
    const [openEditarLista, setOpenEditarLista] = useState(false)
    const [openRemoverLista, setOpenRemoverLista] = useState(false)
    const [openCriarItem, setOpenCriarItem] = useState(false)
    const [openEditarItem, setOpenEditarItem] = useState(false)
    const [openRemoverItem, setOpenRemoverItem] = useState(false)
    const [openConfigPermissao, setOpenConfigPermissao] = useState(false)

    const isAdmin = config?.flagLinksTutoriaisAdmin === 1

    const podeEditarLista = useCallback((idLista?: number) => {
        if (isAdmin) return true
        if (!idLista) {
            return config?.permissoesLista?.some(p => p.flagEditarLista === 1) ?? false
        }
        const perm = config?.permissoesLista?.find(p => p.idLista === idLista)
        return perm?.flagEditarLista === 1
    }, [config, isAdmin])

    const podeEditarItem = useCallback((idLista: number) => {
        if (isAdmin) return true
        const perm = config?.permissoesLista?.find(p => p.idLista === idLista)
        return perm?.flagEditarItem === 1
    }, [config, isAdmin])

    const listaExibida = listaFiltered ?? lista

    const recarregar = useCallback(() => {
        if (userData?.id) {
            getConfig(dispatch, userData.id)
            getListas(dispatch, userData.id)
        }
    }, [dispatch, userData])

    useEffect(() => {
        recarregar()
    }, [recarregar, requestSave.data, requestRemove.data])

    useEffect(() => {
        filterListas(dispatch, lista, fieldFiltered)
    }, [dispatch, lista, fieldFiltered])

    const handleFilter = useCallback(() => {
        filterListas(dispatch, lista, fieldFiltered)
    }, [dispatch, lista, fieldFiltered])

    const handleFecharLista = useCallback(() => {
        setOpenCriarLista(false)
        setOpenEditarLista(false)
    }, [])

    const handleFecharItem = useCallback(() => {
        setOpenCriarItem(false)
        setOpenEditarItem(false)
    }, [])

    const handleSalvarLista = useCallback(async (values: { nome: string }) => {
        const data: any = { nome: values.nome, idUsuario: userData?.id }
        if (openEditarLista && currentLista) data.id = currentLista.id
        await saveLista(dispatch, data)
        handleFecharLista()
    }, [dispatch, userData, openEditarLista, currentLista, handleFecharLista])

    const handleRemoverListaConfirm = useCallback(async () => {
        if (!currentLista) return
        await removeLista(dispatch, { id: currentLista.id, idUsuario: userData?.id })
        setOpenRemoverLista(false)
    }, [dispatch, currentLista, userData])

    const handleSalvarItem = useCallback(async (values: { assunto: string; link: string }) => {
        if (!currentLista) return
        const data: any = {
            assunto: values.assunto,
            link: values.link,
            idLista: currentLista.id,
            idUsuario: userData?.id,
        }
        if (openEditarItem && currentItem) data.id = currentItem.id
        await saveItem(dispatch, data)
        handleFecharItem()
    }, [dispatch, userData, openEditarItem, currentLista, currentItem, handleFecharItem])

    const handleRemoverItemConfirm = useCallback(async () => {
        if (!currentItem) return
        await removeItem(dispatch, { id: currentItem.id, idUsuario: userData?.id })
        setOpenRemoverItem(false)
    }, [dispatch, currentItem, userData])

    const handleMoverLista = useCallback(async (index: number, direcao: 'up' | 'down') => {
        const arr = [...(listaExibida ?? [])]
        const novoIndex = direcao === 'up' ? index - 1 : index + 1
        if (novoIndex < 0 || novoIndex >= arr.length) return
        const temp = arr[index]
        arr[index] = arr[novoIndex]
        arr[novoIndex] = temp
        const ordens = arr.map((item, i) => ({ id: item.id, ordem: i + 1 }))
        await saveOrdem(dispatch, { idUsuario: userData!.id, ordens })
    }, [dispatch, listaExibida, userData])

    if (requestConfig.loading) {
        return <div style={{ padding: '24px' }}>Carregando...</div>
    }

    return (
        <>
            <Section name="links-tutoriais">
                <SectionBox
                    title="Links e Tutoriais"
                    padding={false}
                    right={
                        <>
                            <TextField
                                variant="standard"
                                className="fieldFilter"
                                placeholder="Buscar lista"
                                value={fieldFiltered}
                                onChange={e => dispatch(setFieldFiltered(e.target.value))}
                                onKeyDown={e => e.keyCode === 13 && handleFilter()}
                            />
                            <SectionOption icon={mdiMagnify} tooltip="Filtrar" onClick={handleFilter} />
                            {podeEditarLista() && (
                                <SectionOption
                                    icon={mdiPlusCircle}
                                    tooltip="Criar lista"
                                    onClick={() => setOpenCriarLista(true)}
                                />
                            )}
                        </>
                    }
                >
                    <AgrupamentoComponent
                        lista={listaExibida}
                        loading={requestGet.loading}
                        canEditLista={podeEditarLista()}
                        isAdmin={isAdmin}
                        canEditItem={podeEditarItem}
                        onConfigurarLista={(item: LinksTutoriaisListaType) => {
                            dispatch(setCurrentLista(item))
                            setOpenConfigPermissao(true)
                        }}
                        onEditarLista={(item: LinksTutoriaisListaType) => {
                            dispatch(setCurrentLista(item))
                            setOpenEditarLista(true)
                        }}
                        onRemoverLista={(item: LinksTutoriaisListaType) => {
                            dispatch(setCurrentLista(item))
                            setOpenRemoverLista(true)
                        }}
                        onCriarItem={(item: LinksTutoriaisListaType) => {
                            dispatch(setCurrentLista(item))
                            setOpenCriarItem(true)
                        }}
                        onEditarItem={(listaItem: LinksTutoriaisListaType, row: LinksTutoriaisItemType) => {
                            dispatch(setCurrentLista(listaItem))
                            dispatch(setCurrentItem(row))
                            setOpenEditarItem(true)
                        }}
                        onRemoverItem={(listaItem: LinksTutoriaisListaType, row: LinksTutoriaisItemType) => {
                            dispatch(setCurrentLista(listaItem))
                            dispatch(setCurrentItem(row))
                            setOpenRemoverItem(true)
                        }}
                        onMoverLista={handleMoverLista}
                    />
                </SectionBox>
            </Section>

            <ConfigPermissaoListaModal
                open={openConfigPermissao}
                setOpen={setOpenConfigPermissao}
                lista={currentLista}
                idUsuarioAdmin={userData?.id ?? 0}
                onSalvo={recarregar}
            />

            <Modal width={500} open={openCriarLista || openEditarLista} setOpen={openEditarLista ? setOpenEditarLista : setOpenCriarLista} title={openEditarLista ? 'Editar lista' : 'Nova lista'}>
                <Formik
                    initialValues={{ nome: openEditarLista ? currentLista?.nome ?? '' : '' }}
                    enableReinitialize
                    onSubmit={handleSalvarLista}
                >
                    <FormikContainer>
                        <Form.Container padding={false}>
                            <Form.Row columns={1}>
                                <Form.Group inputID="nome" inputName="nome" label="Nome da lista/tema" />
                            </Form.Row>
                            <div className="modalBoxMainActionsContainer">
                                <button type="reset" className="outline" onClick={handleFecharLista}>Cancelar</button>
                                <button type="submit" className="default">Salvar</button>
                            </div>
                        </Form.Container>
                    </FormikContainer>
                </Formik>
            </Modal>

            <Modal width={500} open={openRemoverLista} setOpen={setOpenRemoverLista} title="Remover lista">
                <span className="modalBoxMessage">Deseja remover esta lista e todos os seus links?</span>
                <div className="modalBoxMainActionsContainer">
                    <button className="outline" onClick={() => setOpenRemoverLista(false)}>Cancelar</button>
                    <button className="default" onClick={handleRemoverListaConfirm}>Remover</button>
                </div>
            </Modal>

            <Modal width={600} open={openCriarItem || openEditarItem} setOpen={openEditarItem ? setOpenEditarItem : setOpenCriarItem} title={openEditarItem ? 'Editar link' : 'Novo link'}>
                <Formik
                    initialValues={{
                        assunto: openEditarItem ? currentItem?.assunto ?? '' : '',
                        link: openEditarItem ? currentItem?.link ?? '' : '',
                    }}
                    enableReinitialize
                    onSubmit={handleSalvarItem}
                >
                    <FormikContainer>
                        <Form.Container padding={false}>
                            <Form.Row columns={1}>
                                <Form.Group inputID="assunto" inputName="assunto" label="Assunto" />
                            </Form.Row>
                            <Form.Row columns={1}>
                                <Form.Group inputID="link" inputName="link" label="Link (URL)" />
                            </Form.Row>
                            <div className="modalBoxMainActionsContainer">
                                <button type="reset" className="outline" onClick={handleFecharItem}>Cancelar</button>
                                <button type="submit" className="default">Salvar</button>
                            </div>
                        </Form.Container>
                    </FormikContainer>
                </Formik>
            </Modal>

            <Modal width={500} open={openRemoverItem} setOpen={setOpenRemoverItem} title="Remover link">
                <span className="modalBoxMessage">Deseja remover este link?</span>
                <div className="modalBoxMainActionsContainer">
                    <button className="outline" onClick={() => setOpenRemoverItem(false)}>Cancelar</button>
                    <button className="default" onClick={handleRemoverItemConfirm}>Remover</button>
                </div>
            </Modal>
        </>
    )
}

export default LinksTutoriais
